import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BookingLink from "@/components/BookingLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/qa1st-logo.webp";

type NavItem = { label: string; path: string };

const whoWeHelp: NavItem[] = [
  { label: "Training Providers", path: "/training-providers" },
  { label: "Awarding & Assessment Organisations", path: "/awarding-organisations" },
  { label: "Businesses", path: "/ai-for-business" },
];

const services: NavItem[] = [
  { label: "Quality & Assurance", path: "/services" },
  { label: "AI Governance & Assurance", path: "/ai-for-business" },
  { label: "AI Transformation", path: "/ai-for-business" },
  { label: "AI Leadership Accelerator", path: "/ai-leadership-accelerator" },
  { label: "Inclusion CPD", path: "/inclusion-cpd" },
  { label: "Bespoke Request", path: "/diagnostic" },
];

const directLinks: NavItem[] = [
  { label: "Insights", path: "/blog" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const linkClass = (path: string) =>
    `text-sm font-medium transition-colors duration-200 hover:text-accent ${
      location.pathname === path ? "text-accent" : "text-muted-foreground"
    }`;

  const groupActive = (items: NavItem[]) => items.some((i) => i.path === location.pathname);

  return (
    <nav className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
      <div className="container-narrow flex items-center justify-between h-16 px-4 md:px-8">
        <Link to="/" className="flex items-center">
          <img src={logo} alt="QA 1st" className="h-10 w-10 rounded object-cover" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className={linkClass("/")}>
            Home
          </Link>

          {[
            { label: "Who We Help", items: whoWeHelp },
            { label: "Services", items: services },
          ].map((group) => (
            <DropdownMenu key={group.label}>
              <DropdownMenuTrigger
                className={`inline-flex items-center gap-1 outline-none text-sm font-medium transition-colors duration-200 hover:text-accent ${
                  groupActive(group.items) ? "text-accent" : "text-muted-foreground"
                }`}
              >
                {group.label}
                <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-popover">
                {group.items.map((item) => (
                  <DropdownMenuItem key={item.label} asChild>
                    <Link to={item.path}>{item.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}

          {directLinks.map((link) => (
            <Link key={link.path} to={link.path} className={linkClass(link.path)}>
              {link.label}
            </Link>
          ))}

          <Button variant="cta" size="sm" asChild>
            <BookingLink>Book Free Call</BookingLink>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-primary"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card px-4 py-4 space-y-4 max-h-[80vh] overflow-y-auto">
          <Link to="/" onClick={() => setMobileOpen(false)} className={`block text-sm font-medium py-1 ${linkClass("/")}`}>
            Home
          </Link>

          {[
            { label: "Who We Help", items: whoWeHelp },
            { label: "Services", items: services },
          ].map((group) => (
            <div key={group.label}>
              <p className="text-xs font-semibold uppercase tracking-widest text-foreground mb-2">{group.label}</p>
              <div className="space-y-2 pl-3 border-l border-border">
                {group.items.map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`block text-sm font-medium ${linkClass(item.path)}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          <div className="space-y-2">
            {directLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block text-sm font-medium ${linkClass(link.path)}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <Button variant="cta" size="sm" className="w-full" asChild>
            <BookingLink onClick={() => setMobileOpen(false)}>Book Free Call</BookingLink>
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
